import { withholdCodeSchema } from "./app-diagnostics.js";
import { z } from "zod";
//#region src/withheld-reads.ts
/**
* The withholding reads of release-engine, gate `cp:dp:read`:
*
* - `GET /v1/releases/data-planes/:dpId/withheld`: `WithheldController.withheld`, the type
*   `DpWithholdReport` of `OperatorApiService.computeWithheldForDp`.
* - `GET /v1/releases/data-planes/withheld/fleet`: `WithheldController.withheldFleet`, the type
*   `FannedFleetRead<FleetWithholdEventsItem>` of `OperatorApiService.computeWithheldForFleet`.
*
* Plan `2026-10-21-operator-tui`, defaults row F1, ruling R-003. The schemas parse the existing
* types `WithholdEventWire` and `FleetWithholdEventsItem`. A type test holds the event schema equal
* to `WithholdEventWire` in both directions, and the item schema assignable to
* `FleetWithholdEventsItem` in one direction.
*
* `no-platform-channel` means "never computed", a different fact from `computed` with no events.
* A reader renders it as not collected, never as a clean data plane.
*/
/**
* One withholding event as release-engine's `DiagnosticsSink` emits it. `appId`, `appSlug` and
* `fixtureId` are `null` when the exit has none in scope. `configKey` is present only for
* `PENDING_REQUIRED_CONFIG`.
*/
const withholdEventWireSchema = z.object({
	code: withholdCodeSchema,
	appId: z.string().nullable(),
	appSlug: z.string().nullable(),
	fixtureId: z.string().nullable(),
	detail: z.string(),
	configKey: z.string().optional()
}).strict();
const computedFields = {
	status: z.literal("computed"),
	channelId: z.string().min(1),
	etag: z.string().min(1),
	events: z.array(withholdEventWireSchema)
};
/**
* `GET /v1/releases/data-planes/:dpId/withheld`: `computed` with the platform channel, the
* envelope ETag and every event in emission order, or `no-platform-channel`.
*/
const dpWithholdReportSchema = z.discriminatedUnion("status", [z.object(computedFields).strict(), z.object({ status: z.literal("no-platform-channel") }).strict()]);
/**
* One data plane of the fleet read: the two arms of {@link dpWithholdReportSchema} keyed by `dpId`,
* plus `unread` with an opaque correlation `ref` when the CP could not compute it.
*/
const fleetWithholdEventsItemSchema = z.discriminatedUnion("status", [
	z.object({
		dpId: z.string().min(1),
		...computedFields
	}).strict(),
	z.object({
		dpId: z.string().min(1),
		status: z.literal("no-platform-channel")
	}).strict(),
	z.object({
		dpId: z.string().min(1),
		status: z.literal("unread"),
		ref: z.string().min(1)
	}).strict()
]);
/**
* `GET /v1/releases/data-planes/withheld/fleet`: one item per active data plane. `partial` is true
* exactly when `failedKeys` is not empty, and each failed key is also an `unread` item.
*/
const fleetWithheldEventsResponseSchema = z.object({
	items: z.array(fleetWithholdEventsItemSchema),
	partial: z.boolean(),
	failedKeys: z.array(z.string().min(1))
}).strict().refine((body) => body.partial === body.failedKeys.length > 0, {
	message: "partial is true exactly when failedKeys is not empty",
	path: ["partial"]
});
//#endregion
export { dpWithholdReportSchema, fleetWithheldEventsResponseSchema, fleetWithholdEventsItemSchema, withholdEventWireSchema };

//# sourceMappingURL=withheld-reads.js.map